import * as React from 'react';
import { nanoid } from 'nanoid';
import { Link } from 'gatsby';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@reach/tabs';
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuPopover,
  MenuLink,
} from '@reach/menu-button';
import PropTypes from 'prop-types';

import { useGraphQL, useThemeContext } from '../hooks';
import { GLFOnlineLandscape } from './vectors';
import { siteNavigation } from '../data/site-navigation';

import { MobileMenu } from './mobile-menu';

function Header() {
  const {
    site: {
      siteMetadata: { title },
    },
  } = useGraphQL();

  // Check if code is running in a browser
  const isBrowser = typeof window !== 'undefined';

  // State for managing mobile menu
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  // State for managing tabs in desktop menu
  const [tabIndex, setTabIndex] = React.useState(2);

  const [showPopover, setShowPopover] = React.useState(false);

  // Array of themes
  const themes = ['ladies', 'mens'];

  // Get current theme for site context
  const { theme } = useThemeContext();

  // Set the active tab to mens if we're on a page that has 'mens' in the URL
  React.useEffect(() => {
    if (theme === 'ladies') setTabIndex(0);
    if (theme === 'mens') setTabIndex(1);
  }, [theme]);

  // Set default tab to be open
  let index = 2;
  if (isBrowser && window.location.href.indexOf('/ladies/') > 0) {
    index = 0;
  }
  if (isBrowser && window.location.href.indexOf('/mens/') > 0) {
    index = 1;
  }

  // Function that is run when clicking on a tab
  function handleChange(i) {
    setTabIndex(i);
  }

  return (
    <header
      data-theme={themes[tabIndex]}
      className="sticky top-0 z-20 bg-white border-t border-b shadow lg:border-none sm:top-12"
    >
      <Tabs defaultIndex={index} index={tabIndex} onChange={handleChange}>
        <div className="flex justify-between w-full lg:overflow-x-auto">
          <Link
            to="/"
            className="flex items-center px-4 py-2 pl-4 lg:border-t lg:border-b lg:flex-1 sm:pl-6 lg:pl-8 focus:outline-none focus:bg-gray-100"
          >
            <h1 className="flex items-center">
              <span className="sr-only">{title}</span>
              <GLFOnlineLandscape className="h-10 text-gray-800" />
            </h1>
          </Link>
          <div className="flex items-center justify-center pr-3 lg:hidden">
            <button
              type="button"
              onClick={() => setIsMenuOpen(true)}
              className="inline-flex items-center justify-center p-2 text-gray-600 transition duration-150 ease-in-out rounded-md hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-200 focus:text-gray-700"
            >
              <span className="sr-only">Open menu</span>
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
          <div className="relative z-10 hidden lg:flex">
            <div className="flex flex-1 space-x-12">
              <TabList
                as="nav"
                className="flex ml-auto border-l border-gray-300 divide-x"
              >
                {siteNavigation.map((navItem) =>
                  navItem.submenu ? (
                    <Tab
                      key={nanoid()}
                      data-theme={navItem.label.toLowerCase()}
                      data-has-submenu
                      onMouseOver={() => {
                        setShowPopover(false);
                        setTabIndex(navItem.label === 'Ladies' ? 0 : 1);
                      }}
                      as={Link}
                      to={navItem.slug}
                      style={{ borderRadius: 0 }}
                      className="inline-flex items-center justify-center flex-1 w-40 space-x-1 text-sm font-bold leading-6 tracking-widest uppercase transition duration-150 ease-in-out bg-white border-0 border-t border-b border-l border-gray-300 border-solid first:border-l-0 group focus:outline-none"
                    >
                      <span className="inline-block pl-2">{navItem.label}</span>
                      <svg
                        className="w-5 h-5 text-gray-600 transition duration-150 ease-in-out group-hover:text-gray-300 group-focus:text-gray-300"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Tab>
                  ) : (
                    <Tab
                      onMouseOver={() => setTabIndex(2)}
                      key={nanoid()}
                      data-has-submenu={false}
                      as={Link}
                      to={navItem.slug}
                      className="relative flex items-center justify-center w-40 text-sm font-bold leading-6 tracking-widest uppercase transition duration-150 ease-in-out bg-white border-t border-b border-l border-gray-300 border-solid focus:outline-none hover:bg-gray-100 focus:bg-gray-200"
                    >
                      {navItem.label}
                    </Tab>
                  )
                )}
              </TabList>
            </div>
          </div>
        </div>
        {tabIndex < 2 && (
          <div className="absolute inset-x-0 hidden shadow h-9 lg:block">
            <TabPanels className="relative">
              {siteNavigation.map(
                (tab) =>
                  tab.submenu && (
                    <TabPanel
                      key={nanoid()}
                      className={`focus:outline-none focus:shadow-outline-primary ${
                        tabIndex === null ? 'hidden' : ''
                      }`}
                    >
                      <nav className="flex items-center px-4 py-2 space-x-2 text-white sm:px-6 lg:px-8 bg-primary">
                        {tab.submenu.map((submenu) => {
                          if (submenu.submenu) {
                            return (
                              <SubMenuDropdown
                                key={nanoid()}
                                submenu={submenu}
                                showPopover={showPopover}
                                setShowPopover={setShowPopover}
                              />
                            );
                          }
                          return (
                            <SubMenuLink key={nanoid()} submenu={submenu} />
                          );
                        })}
                      </nav>
                    </TabPanel>
                  )
              )}
            </TabPanels>
          </div>
        )}
      </Tabs>
      <MobileMenu isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      {/* This is to hide to overflow of the shadow */}
      <div
        aria-hidden
        className="absolute inset-y-0 left-0 w-1 h-full transform -translate-x-1/2 bg-white"
      />
      <div
        aria-hidden
        className="absolute inset-y-0 right-0 w-1 h-full transform translate-x-1/2 bg-white"
      />
    </header>
  );
}

function SubMenuDropdown({ submenu, showPopover, setShowPopover }) {
  return (
    <Menu className="overflow-hidden text-sm font-bold tracking-wider uppercase bg-white rounded focus:outline-none group">
      <MenuButton
        onClick={() => setShowPopover(true)}
        className="relative flex items-center py-1 pl-2 pr-6 overflow-hidden text-sm font-bold leading-none tracking-wider uppercase rounded focus:outline-none focus:shadow-outline-primary focus:bg-primary-light hover:bg-primary-light"
      >
        {submenu.label}
        <span
          aria-hidden
          className="absolute inset-y-0 flex items-center justify-center right-2"
        >
          <svg
            className="w-4 h-4 text-gray-200"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </MenuButton>

      <MenuPopover
        className={`z-30 p-0 mt-2 -mx-3 bg-white rounded shadow-lg outline-none ${
          !showPopover && 'hidden'
        }`}
      >
        <div className="flex py-1 rounded shadow-xs">
          <MenuItems className="flex py-0 border-none">
            <div>
              {submenu.submenu.col1.map((col1) => (
                <MenuLink
                  key={col1.id}
                  as={Link}
                  to={col1.slug}
                  className="px-4 py-1 text-base leading-6 text-gray-700 hover:bg-primary focus:bg-primary"
                >
                  {col1.label}
                </MenuLink>
              ))}
            </div>
            <div>
              {submenu.submenu.col2 &&
                submenu.submenu.col2.map((col2) => (
                  <MenuLink
                    key={col2.id}
                    as={Link}
                    to={col2.slug}
                    className="px-4 py-1 text-base leading-6 text-gray-700 hover:bg-primary focus:bg-primary"
                  >
                    {col2.label}
                  </MenuLink>
                ))}
            </div>
          </MenuItems>
        </div>
      </MenuPopover>
    </Menu>
  );
}

SubMenuDropdown.propTypes = {
  submenu: PropTypes.object.isRequired,
  showPopover: PropTypes.bool,
  setShowPopover: PropTypes.func,
};

function SubMenuLink({ submenu }) {
  return (
    <Link
      to={submenu.slug}
      className="px-2 py-1 overflow-hidden text-sm font-bold leading-none tracking-wider uppercase rounded focus:outline-none focus:shadow-outline-primary focus:bg-primary-light hover:bg-primary-light"
    >
      {submenu.label}
    </Link>
  );
}

SubMenuLink.propTypes = {
  submenu: PropTypes.object.isRequired,
};

export { Header };
